import {
  ApiBlueprint,
  PageBlueprint,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { backendAuthApiRef } from '@internal/plugin-neo4j-auth-common';

import { imsControlPanelApiRef, ImsControlPanelRestApi } from './api';
import { IMS_AUDIT_EVENTS_PATH, IMS_CONTROL_PANEL_BASE_URL } from './const';
import { imsControlPanelReadPermission } from '@internal/backstage-plugin-neo4j-ims-control-panel-common';
import {
  auditEventRouteRef,
  entityDetailRouteRef,
  rootRouteRef,
} from './routes';

const imsControlPanelApi = ApiBlueprint.make({
  name: 'ims-control-panel-api',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: imsControlPanelApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
          backendAuthApi: backendAuthApiRef,
        },
        factory: ({ discoveryApi, fetchApi, backendAuthApi }) =>
          new ImsControlPanelRestApi({
            discoveryApi,
            fetchApi,
            backendAuthApi,
          }),
      }),
    ),
});

const imsControlPanelPage = PageBlueprint.make({
  params: {
    path: IMS_CONTROL_PANEL_BASE_URL,
    routeRef: rootRouteRef,
    noHeader: true,
    loader: () =>
      import('./components/ImsControlPanelPage').then(m => (
        <RequirePermission permission={imsControlPanelReadPermission}>
          <m.ImsControlPanelPage />
        </RequirePermission>
      )),
  },
});

const imsEntityDetailPage = PageBlueprint.make({
  name: 'detail',
  params: {
    path: `${IMS_CONTROL_PANEL_BASE_URL}/:deployment/:environment/:kind/:id`,
    routeRef: entityDetailRouteRef,
    noHeader: true,
    loader: () =>
      import('./components/ImsEntityPage').then(m => (
        <RequirePermission permission={imsControlPanelReadPermission}>
          <m.ImsEntityPage />
        </RequirePermission>
      )),
  },
});

// The static segment ranks above the entity page's `:kind`, so
// `/audit-events/<id>` never resolves as an entity of kind "audit-events".
const imsAuditEventPage = PageBlueprint.make({
  name: 'audit-event',
  params: {
    path: `${IMS_CONTROL_PANEL_BASE_URL}/:deployment/:environment/${IMS_AUDIT_EVENTS_PATH}/:eventId`,
    routeRef: auditEventRouteRef,
    noHeader: true,
    loader: () =>
      import('./components/AuditEventPage').then(m => (
        <RequirePermission permission={imsControlPanelReadPermission}>
          <m.AuditEventPage />
        </RequirePermission>
      )),
  },
});

export const neo4JImsControlPanelPlugin = createFrontendPlugin({
  info: {
    packageJson: () => import('../package.json'),
    manifest: () => import('../catalog-info.yaml'),
  },
  pluginId: 'neo4j-ims-control-panel',
  extensions: [
    imsControlPanelApi,
    imsControlPanelPage,
    imsEntityDetailPage,
    imsAuditEventPage,
  ],
  routes: {
    root: rootRouteRef,
    entityDetail: entityDetailRouteRef,
    auditEvent: auditEventRouteRef,
  },
});
